import { confirm } from 'cadenza/alert';
import type {
  CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload,
  RemoveLocalAttributeCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { showPanel } from 'cadenza/workbook/state/workbook-panel-actions.state';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { isAttributeUsed } from 'cadenza/workbook/workbook-core-selectors.state';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import i18n from './data-browser-attribute-context-actions.properties';

/**
 * Adds the given attribute to the analysis context. The context is
 * displayed if it wasn't at the time of adding the attribute.
 *
 * @param attributeId - ID of the attribute to add.
 * @param activeAttributeId - ID of the active attribute to add, if triggered from a data view bundle.
 */
export function addAttributeToAnalysisContextAction(attributeId: AttributeId, activeAttributeId?: ActiveAttributeId) {
  getStore().dispatch(executeCommand(Command.ADD_CONDITION({ attributeId, activeAttributeId })));
  getStore().dispatch(showPanel(WorkbookPanelName.ANALYSIS_CONTEXT));
}

/**
 * Creates a local attribute with a modified olap attribute definition based on a given attribute
 *
 * @param options - Options used for creating a new local attribute with a modified olap attribute definition.
 * @param options.attributeId - The attributeId of an existing attribute for which a local attribute should be created.
 */
export function createLocalAttributeWithModifiedOlapAttributeDefinitionAction(
  options: CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload,
) {
  getStore().dispatch(executeCommand(Command.CREATE_LOCAL_ATTRIBUTE_WITH_MODIFIED_OLAP_ATTRIBUTE_DEFINITION(options)));
}

/**
 * Removes a local attribute (either a date hierarchy or local attribute with a modified olap
 * attribute definition)
 *
 * @param {object} payload - Object containing the attributeId of the local attribute to remove.
 */
export function removeLocalAttribute(payload: RemoveLocalAttributeCommandPayload) {
  if (isAttributeUsed(getStore().getState(), payload.attributeId)) {
    confirm(i18n('confirmRemovalOfLocalAttribute.title'), i18n('confirmRemovalOfLocalAttribute.message'), {
      type: 'warning',
      submit: i18n('cancel'),
      alternativeSubmit: i18n('confirmRemovalOfLocalAttribute.deleteAnyway'),
      cancel: false,
      alternativeSubmitCallback: () => getStore().dispatch(executeCommand(Command.REMOVE_LOCAL_ATTRIBUTE(payload))),
    });
  } else {
    getStore().dispatch(executeCommand(Command.REMOVE_LOCAL_ATTRIBUTE(payload)));
  }
}
